/**
 * Utility functions for handling day values in opening hours
 * Days are stored as strings: "0" = Monday, "1" = Tuesday, ..., "6" = Sunday
 */

import { useTranslation } from 'react-i18next';

export const DAY_VALUES = ['0', '1', '2', '3', '4', '5', '6'] as const;
export type DayValue = typeof DAY_VALUES[number];

/**
 * Hook to get day display name using i18n
 * @param day - The day value (0-6 as string)
 * @returns The display name for the day in the current language
 */
export function useDayDisplayName(day: DayValue): string {
  const { t } = useTranslation();
  return t(`openingHours.${day}`);
}

/**
 * Get the display name for a day value (for use outside of React components)
 * @param day - The day value (0-6 as string)
 * @param language - The language for display names ('de' or 'en')
 * @returns The display name for the day
 */
export function getDayDisplayName(day: DayValue, language: 'de' | 'en' = 'de'): string {
  // Import translations directly for non-React usage
  const translations = {
    de: {
      '0': 'Montag',
      '1': 'Dienstag',
      '2': 'Mittwoch',
      '3': 'Donnerstag',
      '4': 'Freitag',
      '5': 'Samstag',
      '6': 'Sonntag'
    },
    en: {
      '0': 'Monday',
      '1': 'Tuesday',
      '2': 'Wednesday',
      '3': 'Thursday',
      '4': 'Friday',
      '5': 'Saturday',
      '6': 'Sunday'
    }
  };
  
  return translations[language][day] || day;
}

/**
 * Get the day value from a display name
 * @param displayName - The display name of the day
 * @param language - The language of the display name ('de' or 'en')
 * @returns The day value or null if not found
 */
export function getDayValueFromDisplayName(displayName: string, language: 'de' | 'en' = 'de'): DayValue | null {
  const translations: Record<string, Record<string, DayValue>> = {
    de: {
      'Montag': '0',
      'Dienstag': '1',
      'Mittwoch': '2',
      'Donnerstag': '3',
      'Freitag': '4',
      'Samstag': '5',
      'Sonntag': '6'
    },
    en: {
      'Monday': '0',
      'Tuesday': '1',
      'Wednesday': '2',
      'Thursday': '3',
      'Friday': '4',
      'Saturday': '5',
      'Sunday': '6'
    }
  };
  
  return translations[language][displayName] || null;
}

/**
 * Validate if a day value is valid
 * @param day - The day value to validate
 * @returns True if the day value is valid
 */
export function isValidDayValue(day: string): day is DayValue {
  return DAY_VALUES.includes(day as DayValue);
}

/**
 * Get all day values as an array
 * @returns Array of all day values
 */
export function getAllDayValues(): DayValue[] {
  return [...DAY_VALUES];
}

/**
 * Get all display names for a language
 * @param language - The language for display names ('de' or 'en')
 * @returns Array of display names
 */
export function getAllDayDisplayNames(language: 'de' | 'en' = 'de'): string[] {
  const translations = {
    de: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
    en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  };
  
  return translations[language];
}

const API_WEEKDAY_VALUES: Record<string, DayValue> = {
  monday: '0',
  tuesday: '1',
  wednesday: '2',
  thursday: '3',
  friday: '4',
  saturday: '5',
  sunday: '6',
};

/**
 * Normalize a day value from the API (numeric, API weekday name, or display name) to a DayValue
 */
export function normalizeDayValue(day: string | number | null | undefined): DayValue {
  if (day === null || day === undefined || day === '') {
    return '0';
  }

  const normalized = String(day).trim();

  if (isValidDayValue(normalized)) {
    return normalized;
  }

  const apiWeekday = API_WEEKDAY_VALUES[normalized.toLowerCase()];
  if (apiWeekday) {
    return apiWeekday;
  }

  const fromDe = getDayValueFromDisplayName(normalized, 'de');
  if (fromDe) {
    return fromDe;
  }

  const fromEn = getDayValueFromDisplayName(normalized, 'en');
  if (fromEn) {
    return fromEn;
  }

  return '0';
} 